'use client';

import type React from 'react';
import { useState } from 'react';

import type { ExpenseStatus, PeriodByDate } from '@/app/models/period';
import { useExportPeriodFileMutation, usePeriodFileByPeriodQuery } from '@/app/services/periodFileApi';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import DataTable, { bodyCellSx, type Column } from '@/components/common/DataTable';
import CancelOrConfirmDialog from '@/components/common/dialogs/CancelOrConfirmDialog';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReplayIcon from '@mui/icons-material/Replay';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, Button, IconButton, Menu, MenuItem, TableCell, Typography, useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import { Trans, useTranslation } from 'react-i18next';

interface GeneralTableProps {
  periods: PeriodByDate[];
  authorizeAction: (periodDate: string) => void;
  closePeriodAction: (periodDate: string) => void;
  authorizeMontlyExpenseAction: (periodDate: string) => void;
}

type DialogType = 'process' | 'reconfigure' | 'close' | null;

export default function GeneralTable({
  periods,
  authorizeAction,
  closePeriodAction,
  authorizeMontlyExpenseAction,
}: GeneralTableProps) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { t, i18n } = useTranslation(['general', 'common']);

  const mainPeriod = periods.find((p) => p.branchName === 'Geral');

  const [activeDialog, setActiveDialog] = useState<DialogType>(null);

  const { data: files } = usePeriodFileByPeriodQuery(mainPeriod?.id as number, {
    skip: !selectedId,
  });
  const [exportFiles] = useExportPeriodFileMutation();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);

    if (mainPeriod?.status === 'CLOSED') {
      exportFiles(mainPeriod.date);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedId(null);
  };

  const getStatusSx = (status: ExpenseStatus) => {
    const baseStyles = {
      py: 0.5,
      px: 1,
      borderRadius: 1,
      fontSize: 'caption.fontSize',
      fontWeight: 'medium',
    };

    switch (status) {
      case 'OPENED':
        return {
          ...baseStyles,
          backgroundColor: 'warning.main',
          color: 'warning.contrastText',
        };
      case 'PENDING':
        return {
          ...baseStyles,
          backgroundColor: 'info.light',
          color: 'text.primary',
        };
      case 'CLOSED':
        return {
          ...baseStyles,
          backgroundColor: 'success.main',
          color: 'success.contrastText',
        };
      default:
        return {
          ...baseStyles,
          backgroundColor: 'info.light',
          color: 'text.primary',
        };
    }
  };

  const handleDialogClose = () => {
    setActiveDialog(null);
    handleMenuClose();
  };

  const getPeriodText = () => {
    if (!mainPeriod || !mainPeriod.date) return '-- - --';
    const date = DateTime.fromISO(mainPeriod.date);
    return `${date.setLocale(i18n.language).monthLong} - ${date.year}`;
  };

  const getDialogProps = (dialogType: DialogType) => {
    if (!dialogType) return null;

    const configs = {
      process: {
        title: 'confirm.process',
        contentText: 'process.text',
      },
      reconfigure: {
        title: 'confirm.reconfigure',
        contentText: 'reconfigure.text',
      },
      close: {
        title: 'confirm.close',
        contentText: 'close.text',
      },
    };

    return {
      title: t(configs[dialogType].title),
      contentText: <Trans>{t(configs[dialogType].contentText, { period: getPeriodText() })}</Trans>,
    };
  };

  const columns: Column[] = [
    { key: 'branchName', label: t('common:branch') },
    { key: 'directValue', label: t('direct.value') },
    { key: 'availableValue', label: t('available.value') },
    { key: 'overheadValue', label: `${t('overhead.value')} (${t('overhead')})` },
    { key: 'valueToPay', label: `${t('value.to.pay')} (${t('overhead')})` },
    { key: 'sharedValue', label: t('shared.value') },
    { key: 'difference', label: t('difference') },
    { key: 'status', label: t('status') },
    { key: 'actions', label: t('actions'), align: 'right' },
  ];

  const renderRow = (row: PeriodByDate) => (
    <>
      <TableCell sx={bodyCellSx}>
        <Typography variant="body2" fontWeight="small">
          {row.branchName}
        </Typography>
      </TableCell>
      <TableCell sx={bodyCellSx}>{`${formatterMonetaryValue(row.directValue || '')}`}</TableCell>
      <TableCell sx={bodyCellSx}>{`${formatterMonetaryValue(row.availableValue || '')}`}</TableCell>
      <TableCell sx={bodyCellSx}>{`${formatterMonetaryValue(row.overheadValue || '')}`}</TableCell>
      <TableCell sx={bodyCellSx}>{`${formatterMonetaryValue(row.overheadValueToPay || '')}`}</TableCell>
      <TableCell sx={bodyCellSx}>{`${formatterMonetaryValue(row.sharedValue || '')}`}</TableCell>
      <TableCell sx={bodyCellSx}>{formatterMonetaryValue(row.availableValue - row.sharedValue)}</TableCell>
      <TableCell sx={bodyCellSx}>
        {row.status && (
          <Box component="span" sx={getStatusSx(row.status)}>
            {t(row.status)}
          </Box>
        )}
      </TableCell>
      <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
        {row.id && row.branchName === 'Geral' && (
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, row.id)} aria-label="more actions">
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}
      </TableCell>
    </>
  );

  const getRowKey = (row: PeriodByDate) => `${row.id || 'no-id'}-${row.date}-${row.branchName}`;

  const dialogProps = getDialogProps(activeDialog);

  return (
    <>
      <DataTable
        columns={columns}
        data={periods}
        renderRow={renderRow}
        getRowKey={getRowKey}
        emptyMessage=""
        stickyHeader={false}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {mainPeriod?.status !== 'CLOSED' && (
          <MenuItem onClick={() => setActiveDialog('process')}>
            <SettingsIcon fontSize="small" sx={{ mr: 0.75 }} />
            {t('process')}
          </MenuItem>
        )}
        <MenuItem onClick={() => setActiveDialog('reconfigure')}>
          <ReplayIcon fontSize="small" sx={{ mr: 0.75 }} />
          {t('reconfigure')}
        </MenuItem>
        {mainPeriod?.status === 'OPENED' && (
          <MenuItem onClick={() => setActiveDialog('close')}>
            <CancelIcon fontSize="small" sx={{ mr: 0.75 }} />
            {t('close')}
          </MenuItem>
        )}
        {mainPeriod?.status === 'CLOSED' &&
          !!selectedId &&
          !!files &&
          [...files.files]
            .sort((a, b) => a.fileType.localeCompare(b.fileType))
            .filter((file) => file.fileType !== 'PROVIDER_CONFIG')
            .map((file) => (
              <MenuItem key={file.filePath} onClick={() => {}} href={file.filePath}>
                <Button variant="text" sx={{ p: 0, color: 'primary' }} href={file.filePath}>
                  <DownloadIcon
                    fontSize="small"
                    sx={{
                      mr: 0.75,
                      color: theme.palette.text.primary,
                    }}
                  />
                  <Typography
                    sx={{
                      textTransform: 'none',
                      color: theme.palette.text.primary,
                    }}
                  >
                    {t(file.fileType)}
                  </Typography>
                </Button>
              </MenuItem>
            ))}
      </Menu>

      {dialogProps && (
        <CancelOrConfirmDialog
          title={dialogProps.title}
          contentText={dialogProps.contentText}
          open={activeDialog !== null}
          onClose={() => handleDialogClose()}
          onConfirm={() => {
            if (activeDialog === 'process' && mainPeriod) {
              authorizeAction(mainPeriod.date);
            }
            if (activeDialog === 'close' && mainPeriod) {
              closePeriodAction(mainPeriod.date);
            }
            if (activeDialog === 'reconfigure' && mainPeriod) {
              authorizeMontlyExpenseAction(mainPeriod.date);
            }
            handleDialogClose();
          }}
        />
      )}
    </>
  );
}
